// Globals
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { categoriesSelector, editCategory, getCategoryDetails } from '../../feature';

// Components
import { Button } from '@/ui';
import { CheckmarkIcon } from '@/icons';
import { showSuccessToast, useForm } from '@/components';
import { SeoForm } from '../../components';
import { StyledTabTitleWrapper, StyledPageTitle, StyledContentWrapper } from '../../components/Styled';

// Models
import { ICategoryNameLocale } from '@/models/categories.model';

// Helpers
import { EditCategoryDTO } from '../../dtos';
import { EditCategoryFieldNames } from '../../helpers/types';
import { Resource, usePermissions } from '@/modules/Permissions';

const CategoriesSEOContainer = () => {
  const { t } = useTranslation(['categories']);
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const { canManage } = usePermissions();
  const canManageCategories = canManage(Resource.CATEGORY);
  const { categoryDetails, isLoading } = useAppState(categoriesSelector);

  const handleEditSEO = useCallback(async () => {
    try {
      const values = form.getFieldsValue();
      type LocaleKey = keyof typeof values.locale;

      const updatedLocales = categoryDetails?.locale.map((localeItem) => {
        const key = localeItem.locale as LocaleKey;
        const override = values.locale[key];

        return {
          ...localeItem,
          seo_title: override?.seo_title,
          seo_description: override?.seo_description,
          keywords: override?.seo_keywords,
          seo_text: override?.seo_text,
        };
      });

      const editCategoryDto = new EditCategoryDTO({
        [EditCategoryFieldNames.LOCALE]: updatedLocales as ICategoryNameLocale[],
        slug: categoryDetails?.slug,
        google_feed_id: categoryDetails?.google_feed_id,
        [EditCategoryFieldNames.IS_RESTORATION]: categoryDetails?.is_restoration ?? false,
        [EditCategoryFieldNames.IS_HEAVY]: categoryDetails?.is_heavy === true ? true : null,
        ...(categoryDetails?.parent_id && { parent_id: categoryDetails?.parent_id }),
      });

      await dispatch(editCategory({ id: id as string, data: editCategoryDto })).unwrap();
      dispatch(getCategoryDetails(id as string));
      showSuccessToast({ message: t('edit_category_success') });
    } catch {}
  }, [categoryDetails, id]);

  return (
    <>
      <StyledTabTitleWrapper>
        <StyledPageTitle>{t('category_seo_tab_title')}</StyledPageTitle>

        {canManageCategories && (
          <Button type="primary" icon={<CheckmarkIcon />} loading={isLoading} onClick={handleEditSEO}>
            {t('save_category_details')}
          </Button>
        )}
      </StyledTabTitleWrapper>

      <StyledContentWrapper>
        <SeoForm formInstance={form} onSubmit={handleEditSEO} categoryDetails={categoryDetails} isLoading={false} />
      </StyledContentWrapper>
    </>
  );
};

export default CategoriesSEOContainer;
